// Chatbot — /chatbot-settings. The chat widget on the tenant's website: its
// identity, colours, home screen, launcher bubble and installation, one
// chatbot per brand (+ the default one). The live preview on the right is
// the widget itself (ChatbotPreview mirrors chatbotStatic/chatbot.js), styled
// by the tenant's settings. API: GET/POST/PUT/DELETE /api/chatbot/settings,
// POST /api/chatbot/logo, GET /api/chatbot/embed, GET /api/brands/list,
// GET /api/statistics/overview (30-day tickets of the « Website » channel).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bot, ChevronRight, Code, Contact, MessageCircle, MessageSquareText, MessagesSquare, Palette, Plus, Sparkles, Trash2, X,
} from 'lucide-react';
import ChatbotPreview from './ChatbotPreview';
import {
  AttentionCard, BrandColorPicker, BrandPicker, ColorField, DiscardDialog, EmbedCode, ImageDrop, KpiSkeletons,
  PageSkeleton, PreviewCard, RangeField, SaveBar, SwitchRow, authHeaders, channelTraffic, contrastRatio, copyText,
  formatPercent, formatRatio, kitLabels, scrollToId, useChannelOverview,
} from './ChannelsKit';
import {
  Badge, Banner, Card, ConfirmDialog, Field, Kpi, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import './ChatbotSettings.css';

// What a new chatbot starts from: French names and greeting (Kevin 29/10) —
// the page sends them in the POST, the backend keeps what it receives. An
// empty welcome_title lets the widget show its own title in the chatbot's
// language.
export const DEFAULT_SETTINGS = {
  primary_color: '#1F2937',
  secondary_color: '#FFFFFF',
  tertiary_color: '#F8F9FA',
  company_name: 'Votre entreprise',
  agent_name: 'Assistant',
  greetings_message: 'Bonjour ! Comment puis-je vous aider ?',
  brand_id: null,
  displayed: true,
  bubble_shape: 'circle',
  bubble_size: 60,
  bubble_position: 'bottom_right',
  adaptive_contrast: false,
  bubble_border_color: null,
  bubble_shadow: 'default',
  logo_key: null,
  subtitle: '',
  suggested_questions: [],
  teaser_enabled: false,
  teaser_message: '',
  teaser_delay_seconds: 6,
  welcome_title: '',
  header_color: null,
  language: 'fr',
  powered_by: true,
};

// The names a chatbot shows before anyone personalised it: the backend's
// auto-created default (GET /settings) and the page's own new-chatbot drafts
// (before and since 29/10).
const PLACEHOLDER_NAMES = ['Company Name', 'Your Company', 'Votre entreprise'];

export const extractSettings = (chatbot) => ({
  primary_color: chatbot.primary_color,
  secondary_color: chatbot.secondary_color,
  tertiary_color: chatbot.tertiary_color,
  company_name: chatbot.company_name,
  agent_name: chatbot.agent_name,
  greetings_message: chatbot.greetings_message,
  brand_id: chatbot.brand_id,
  displayed: chatbot.displayed ?? true,
  bubble_shape: chatbot.bubble_shape || 'circle',
  bubble_size: chatbot.bubble_size || 60,
  bubble_position: chatbot.bubble_position || 'bottom_right',
  adaptive_contrast: chatbot.adaptive_contrast ?? false,
  bubble_border_color: chatbot.bubble_border_color || null,
  bubble_shadow: chatbot.bubble_shadow || 'default',
  logo_key: chatbot.logo_key || null,
  subtitle: chatbot.subtitle || '',
  suggested_questions: chatbot.suggested_questions || [],
  teaser_enabled: chatbot.teaser_enabled ?? false,
  teaser_message: chatbot.teaser_message || '',
  teaser_delay_seconds: chatbot.teaser_delay_seconds || 6,
  welcome_title: chatbot.welcome_title || '',
  header_color: chatbot.header_color || null,
  language: chatbot.language || 'fr',
  powered_by: chatbot.powered_by ?? true,
});

// « À vérifier » of the chatbot being edited: what its visitors would get
// wrong. `traffic` is the account's 30-day « Website » channel (or null).
export const chatbotChecks = (settings, traffic, { saved }) => {
  const checks = [];
  const name = String(settings.company_name || '').trim();
  if (!name || PLACEHOLDER_NAMES.includes(name)) checks.push({ key: 'defaultName', tone: 'warning', name });
  if (saved && settings.displayed === false) checks.push({ key: 'hidden', tone: 'warning' });
  const ratio = contrastRatio(settings.primary_color);
  if (ratio !== null && ratio < 4.5) checks.push({ key: 'contrast', tone: 'warning', ratio });
  if (saved && settings.displayed !== false && traffic && traffic.count === 0) checks.push({ key: 'noTraffic', tone: 'warning' });
  return checks;
};

const ChatbotSettings = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const kit = kitLabels(t);
  const overview = useChannelOverview();

  const [user, setUser] = useState(null);
  const [chatbotsList, setChatbotsList] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedChatbotId, setSelectedChatbotId] = useState(null);
  const [creating, setCreating] = useState(null); // null | { brandId }
  const [editSettings, setEditSettings] = useState({ ...DEFAULT_SETTINGS });
  const [baseline, setBaseline] = useState({ settings: { ...DEFAULT_SETTINGS }, logoUrl: null });
  const [logoUrl, setLogoUrl] = useState(null); // presigned, display only
  const [logoUploading, setLogoUploading] = useState(false);
  const [questionDraft, setQuestionDraft] = useState('');
  const [previewMode, setPreviewMode] = useState('home');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [embed, setEmbed] = useState({ code: '', loading: false, error: '' });
  const [pendingEntry, setPendingEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const selectedChatbot = chatbotsList.find((c) => c.id === selectedChatbotId);
  const dirty = JSON.stringify(editSettings) !== JSON.stringify(baseline.settings) || logoUrl !== baseline.logoUrl;

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const applyChatbot = useCallback((chatbot) => {
    const settings = extractSettings(chatbot);
    setSelectedChatbotId(chatbot.id);
    setCreating(null);
    setEditSettings(settings);
    setLogoUrl(chatbot.logo_url || null);
    setBaseline({ settings, logoUrl: chatbot.logo_url || null });
    setQuestionDraft('');
  }, []);

  const startCreate = (brandId) => {
    const settings = { ...DEFAULT_SETTINGS, brand_id: brandId || null };
    setSelectedChatbotId(null);
    setCreating({ brandId: brandId || null });
    setEditSettings(settings);
    setLogoUrl(null);
    setBaseline({ settings, logoUrl: null });
    setQuestionDraft('');
  };

  const fetchChatbots = useCallback(async () => {
    const response = await axios.get('/api/chatbot/settings', { headers: authHeaders() });
    const list = response.data || [];
    setChatbotsList(list);
    return list;
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const userRes = await axios.get('/api/users/user', { headers: authHeaders() });
        setUser(userRes.data);
        const list = await fetchChatbots();
        if (list.length) applyChatbot(list[0]);
        if (userRes.data?.has_brand) {
          axios.get('/api/brands/list', { headers: authHeaders() })
            .then((res) => setBrands(res.data || []))
            .catch(() => setBrands([]));
        }
      } catch (err) {
        setLoadError(t('chatbotSettings.loadError', 'Échec du chargement des paramètres'));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, fetchChatbots, applyChatbot, t]);

  // The embed code of the chatbot as saved (its brand decides the script tag).
  const embedBrandId = selectedChatbot?.brand_id || '';
  useEffect(() => {
    if (!selectedChatbotId) {
      setEmbed({ code: '', loading: false, error: '' });
      return undefined;
    }
    let alive = true;
    setEmbed({ code: '', loading: true, error: '' });
    const params = embedBrandId ? `?brand_id=${embedBrandId}` : '';
    axios.get(`/api/chatbot/embed${params}`, { headers: authHeaders() })
      .then((res) => { if (alive) setEmbed({ code: res.data?.embed_code || '', loading: false, error: '' }); })
      .catch(() => { if (alive) setEmbed({ code: '', loading: false, error: t('chatbotSettings.embedError', 'Échec de la génération du code d’intégration') }); });
    return () => { alive = false; };
  }, [selectedChatbotId, embedBrandId, t]);

  const set = (patch) => setEditSettings((prev) => ({ ...prev, ...patch }));

  const getBrandName = (brandId) => {
    if (!brandId) return t('chatbotSettings.defaultChatbot', 'Chatbot par défaut');
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : t('chatbotSettings.unknownBrand', 'Marque inconnue');
  };

  // ---------------------------------------------------------------- actions

  const handleLogoFile = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post('/api/chatbot/logo', form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      set({ logo_key: response.data.logo_key });
      setLogoUrl(response.data.logo_url);
    } catch (err) {
      setToast({ tone: 'danger', text: err.response?.data?.detail || t('chatbotSettings.logoError', 'Échec de l’envoi du logo (PNG, JPEG ou WebP, 2 Mo max).') });
    } finally {
      setLogoUploading(false);
    }
  };

  const addSuggestedQuestion = () => {
    const question = questionDraft.trim();
    if (!question) return;
    setEditSettings((prev) => {
      if ((prev.suggested_questions || []).length >= 4) return prev;
      return { ...prev, suggested_questions: [...(prev.suggested_questions || []), question.slice(0, 80)] };
    });
    setQuestionDraft('');
  };

  const removeSuggestedQuestion = (index) => setEditSettings((prev) => ({
    ...prev,
    suggested_questions: (prev.suggested_questions || []).filter((_, i) => i !== index),
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedChatbotId) {
        await axios.put(`/api/chatbot/settings/${selectedChatbotId}`, editSettings, { headers: authHeaders() });
        const list = await fetchChatbots();
        const fresh = list.find((c) => c.id === selectedChatbotId);
        if (fresh) applyChatbot(fresh);
        setToast({ tone: 'success', text: t('chatbotSettings.updateSuccess', 'Paramètres enregistrés avec succès') });
      } else {
        const response = await axios.post('/api/chatbot/settings', editSettings, { headers: authHeaders() });
        const list = await fetchChatbots();
        const created = list.find((c) => c.id === response.data?.id) || list[list.length - 1];
        if (created) applyChatbot(created);
        setToast({ tone: 'success', text: t('chatbotSettings.createSuccess', 'Chatbot créé avec succès') });
      }
    } catch (err) {
      setToast({ tone: 'danger', text: err.response?.data?.detail || t('chatbotSettings.saveError', 'Échec de l’enregistrement des paramètres') });
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    setDeleteBusy(true);
    try {
      await axios.delete(`/api/chatbot/settings/${selectedChatbotId}`, { headers: authHeaders() });
      const list = await fetchChatbots();
      if (list.length) applyChatbot(list[0]);
      else startCreate(null);
      setToast({ tone: 'success', text: t('chatbotSettings.deleteSuccess', 'Chatbot supprimé avec succès') });
    } catch (err) {
      setToast({ tone: 'danger', text: t('chatbotSettings.deleteError', 'Échec de la suppression du chatbot') });
    } finally {
      setDeleteBusy(false);
      setDeleting(false);
    }
  };

  const copyEmbed = async () => {
    const ok = await copyText(embed.code);
    setToast(ok ? { tone: 'success', text: kit.embed.copiedToast } : { tone: 'danger', text: kit.embed.copyFailed });
  };

  // ---------------------------------------------------------------- derived

  const hasBrand = Boolean(user?.has_brand);
  const traffic = useMemo(() => channelTraffic(overview.overview, 'Website'), [overview.overview]);
  const selectedKey = creating ? (creating.brandId ? `new:${creating.brandId}` : 'new') : selectedChatbotId;
  const editedName = creating
    ? (creating.brandId ? getBrandName(creating.brandId) : t('chatbotSettings.createChatbot', 'Nouveau chatbot'))
    : getBrandName(selectedChatbot?.brand_id);

  const applyEntry = (entry) => {
    if (entry.chatbot) applyChatbot(entry.chatbot);
    else startCreate(entry.brandId);
  };

  const requestEntry = (entry) => {
    if (entry.key === selectedKey) return;
    if (dirty) setPendingEntry(entry);
    else applyEntry(entry);
  };

  const brandEntries = hasBrand ? [
    ...chatbotsList.map((chatbot) => ({
      key: chatbot.id,
      chatbot,
      name: getBrandName(chatbot.brand_id),
      meta: [chatbot.company_name, chatbot.agent_name].filter(Boolean).join(' · '),
      color: chatbot.primary_color,
      badge: (chatbot.displayed ?? true)
        ? <Badge tone="success" dot>{kit.visible}</Badge>
        : <Badge>{kit.hidden}</Badge>,
    })),
    ...brands.filter((brand) => !chatbotsList.some((c) => c.brand_id === brand.id)).map((brand) => ({
      key: `new:${brand.id}`,
      brandId: brand.id,
      name: brand.name,
      meta: t('channelsUi.chatbot.noChatbot', 'Aucun chatbot : le créer'),
      badge: <Badge>{kit.notConfigured}</Badge>,
    })),
    { key: 'new', brandId: null, create: true, name: t('chatbotSettings.createChatbot', 'Nouveau chatbot') },
  ] : [];

  const visibleCount = chatbotsList.filter((c) => c.displayed ?? true).length;
  // A new chatbot takes a brand that has none yet (the POST answers 409 otherwise).
  const takenBrandIds = new Set(chatbotsList.map((chatbot) => chatbot.brand_id || ''));
  const freeBrands = brands.filter((brand) => !takenBrandIds.has(brand.id) || brand.id === editSettings.brand_id);
  const defaultSlotFree = !takenBrandIds.has('') || !editSettings.brand_id;
  const checks = chatbotChecks(editSettings, traffic, { saved: Boolean(selectedChatbotId) });
  const checkText = (check) => {
    switch (check.key) {
    case 'defaultName':
      return {
        title: check.name
          ? t('channelsUi.chatbot.checkDefaultName', 'Vos clients voient le nom « {{name}} »', { name: check.name })
          : t('channelsUi.chatbot.checkNoName', 'Le chatbot n’a pas de nom d’entreprise'),
        detail: t('channelsUi.chatbot.checkDefaultNameDetail', 'C’est le nom par défaut : remplacez-le par celui de votre entreprise ou de la marque.'),
        action: { label: t('channelsUi.chatbot.openIdentity', 'Modifier l’identité'), onClick: () => scrollToId('chatbot-identity') },
      };
    case 'hidden':
      return {
        title: t('channelsUi.chatbot.checkHidden', 'Le chatbot est masqué : il n’apparaît pas sur votre site'),
        detail: t('channelsUi.chatbot.checkHiddenDetail', 'Le code reste installé ; réactivez l’affichage pour que vos clients le revoient.'),
        action: { label: t('channelsUi.openInstall', 'Voir l’installation'), onClick: () => scrollToId('chatbot-install') },
      };
    case 'contrast':
      return {
        title: t('channelsUi.checkContrast', 'Contraste faible ({{ratio}}:1) : le texte sur votre couleur est peu lisible', { ratio: formatRatio(check.ratio, lang) }),
        detail: t('channelsUi.checkContrastDetail', 'Choisissez une couleur plus foncée ou plus claire (4,5:1 au moins).'),
        action: { label: t('channelsUi.openColors', 'Voir les couleurs'), onClick: () => scrollToId('chatbot-theme') },
      };
    default:
      return {
        title: t('channelsUi.chatbot.checkNoTraffic', 'Aucun ticket reçu par le chatbot depuis 30 jours'),
        detail: t('channelsUi.chatbot.checkNoTrafficDetail', 'Vérifiez que le code d’intégration est bien collé sur votre site.'),
        action: { label: t('channelsUi.openCode', 'Voir le code'), onClick: () => scrollToId('chatbot-install') },
      };
    }
  };

  const header = (
    <PageHeader
      title={t('channelsUi.chatbot.title', 'Chatbot')}
      subtitle={hasBrand && chatbotsList.length > 1
        ? t('channelsUi.chatbot.subtitleCount', '{{count}} chatbots · {{visible}} visibles sur vos sites', { count: chatbotsList.length, visible: visibleCount })
        : t('channelsUi.chatbot.subtitle', 'Le widget de discussion de votre site : vos clients écrivent, l’agent IA répond.')}
      actions={selectedChatbotId ? (
        <>
          <OverflowMenu
            label={kit.moreActions}
            items={[{ label: t('channelsUi.chatbot.delete', 'Supprimer ce chatbot'), icon: Trash2, danger: true, onClick: () => setDeleting(true) }]}
          />
          <button type="button" className="u-btn u-btn--secondary" onClick={copyEmbed} disabled={!embed.code}>
            <Code aria-hidden="true" />
            {kit.embed.copyCode}
          </button>
        </>
      ) : null}
    />
  );

  if (loading) {
    return (
      <div className="u-page u-page--narrow chan-v2" aria-busy="true" aria-label={t('chatbotSettings.loading', 'Chargement des paramètres du chatbot...')}>
        <PageSkeleton header={header} />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="u-page u-page--narrow chan-v2">
        {header}
        <div className="u-well"><Banner tone="danger">{loadError}</Banner></div>
      </div>
    );
  }

  const questions = editSettings.suggested_questions || [];
  const onSite = selectedChatbot ? (selectedChatbot.displayed ?? true) : null;

  return (
    <div className="u-page u-page--narrow chan-v2">
      {header}

      <div className="u-well">
        {brandEntries.length > 0 && (
          <BrandPicker label={kit.brands} entries={brandEntries} selectedKey={selectedKey} onSelect={requestEntry} />
        )}

        <AttentionCard
          items={checks.map((check) => ({ ...check, ...checkText(check) }))}
          title={kit.attention}
          criticalLabel={kit.critical}
          watchLabel={kit.watch}
        />

        {overview.status === 'loading' && <KpiSkeletons />}
        {traffic && (
          <div className="u-kpi-row">
            <Kpi
              icon={MessagesSquare}
              label={t('channelsUi.chatbot.kpiTickets', 'Tickets du chatbot')}
              value={traffic.count.toLocaleString(lang)}
              context={t('channelsUi.kpiTicketsContext', '30 derniers jours · {{share}} des tickets du compte', { share: formatPercent(traffic.share, lang) })}
            />
            {traffic.count > 0 && (
              <Kpi
                icon={Sparkles}
                tone="success"
                label={t('channelsUi.kpiAi', 'Résolus par l’IA')}
                value={formatPercent(traffic.aiRate, lang)}
                meter={{ value: traffic.aiRate, max: 100, tone: 'success', label: t('channelsUi.kpiAi', 'Résolus par l’IA') }}
                context={t('channelsUi.kpiAiContext', '{{resolved}} tickets sur {{total}} · 30 jours', { resolved: traffic.aiResolved.toLocaleString(lang), total: traffic.count.toLocaleString(lang) })}
              />
            )}
            {hasBrand && chatbotsList.length > 1 ? (
              <Kpi
                icon={Bot}
                tone="info"
                label={t('channelsUi.chatbot.kpiVisible', 'Chatbots visibles')}
                value={visibleCount}
                unit={`/ ${chatbotsList.length}`}
                context={chatbotsList.length - visibleCount > 0
                  ? t('channelsUi.chatbot.kpiHidden', '{{count}} masqués', { count: chatbotsList.length - visibleCount })
                  : t('channelsUi.chatbot.kpiAllVisible', 'tous affichés sur vos sites')}
              />
            ) : onSite !== null && (
              <Kpi
                icon={Bot}
                tone={onSite ? 'info' : 'neutral'}
                label={t('channelsUi.chatbot.kpiOnSite', 'Sur votre site')}
                value={onSite ? kit.visible : kit.hidden}
                context={[
                  selectedChatbot.bubble_position === 'bottom_left' ? t('chatbotSettings.bottomLeft', 'En bas à gauche') : t('chatbotSettings.bottomRight', 'En bas à droite'),
                  selectedChatbot.language === 'en' ? 'English' : 'Français',
                ].join(' · ')}
              />
            )}
          </div>
        )}

        <div className="chan-v2__layout">
          <div className="chan-v2__column">
            {creating && (
              <Banner tone="info" title={t('channelsUi.chatbot.newTitle', 'Nouveau chatbot · {{name}}', { name: editedName })}>
                {t('channelsUi.chatbot.newHint', 'Réglez son apparence puis créez-le : son code d’intégration apparaîtra dans « Installation ».')}
              </Banner>
            )}

            <Card
              id="chatbot-identity"
              icon={Contact}
              title={t('chatbotSettings.sectionIdentity', 'Identité')}
              description={t('channelsUi.chatbot.identityDesc', 'Le nom et le logo que vos clients voient dans la bulle, l’en-tête et les réponses.')}
            >
              <div className="u-form">
                {hasBrand && (creating ? (
                  <Field
                    label={t('channelsUi.chatbot.brand', 'Marque associée')}
                    htmlFor="brand_id"
                    help={t('settingsPolishUi.chatbot.brandAtCreation', 'Se choisit à la création : chaque marque a un seul chatbot.')}
                  >
                    <select
                      id="brand_id"
                      className="u-select"
                      value={editSettings.brand_id || ''}
                      onChange={(e) => {
                        const brandId = e.target.value === '' ? null : e.target.value;
                        set({ brand_id: brandId });
                        setCreating({ brandId });
                      }}
                    >
                      {defaultSlotFree && <option value="">{t('chatbotSettings.defaultChatbot', 'Chatbot par défaut')}</option>}
                      {freeBrands.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
                    </select>
                  </Field>
                ) : (
                  <Field
                    label={t('channelsUi.chatbot.brand', 'Marque associée')}
                    htmlFor="brand_id"
                    help={t('settingsPolishUi.chatbot.brandLocked', 'Choisie à la création du chatbot : pour une autre marque, créez son chatbot.')}
                  >
                    <input id="brand_id" className="u-input" value={getBrandName(editSettings.brand_id)} readOnly disabled />
                  </Field>
                ))}
                <ImageDrop
                  label={t('chatbotSettings.logo', 'Logo')}
                  imageUrl={logoUrl}
                  fallback={(editSettings.company_name || '?').charAt(0).toUpperCase()}
                  hint={t('chatbotSettings.logoHint', 'Glissez un logo (PNG, JPEG, WebP) — utilisé dans la bulle, l’en-tête et l’avatar')}
                  uploading={logoUploading}
                  uploadingLabel={t('chatbotSettings.logoUploading', 'Envoi…')}
                  onFile={handleLogoFile}
                  onRemove={() => { set({ logo_key: null }); setLogoUrl(null); }}
                  removeLabel={t('chatbotSettings.logoRemove', 'Retirer le logo')}
                />
                <div className="u-form-grid">
                  <Field label={t('chatbotSettings.companyName', 'Nom de l’entreprise')} htmlFor="company_name">
                    <input id="company_name" className="u-input" value={editSettings.company_name || ''} onChange={(e) => set({ company_name: e.target.value })} />
                  </Field>
                  <Field label={t('chatbotSettings.aiAgentName', 'Nom de l’agent')} htmlFor="agent_name">
                    <input id="agent_name" className="u-input" value={editSettings.agent_name || ''} onChange={(e) => set({ agent_name: e.target.value })} />
                  </Field>
                </div>
                <Field
                  label={t('chatbotSettings.subtitle', 'Sous-titre')}
                  htmlFor="subtitle"
                  help={t('channelsUi.chatbot.subtitleHelp', 'Sous le nom de l’agent, 60 caractères au plus.')}
                >
                  <input
                    id="subtitle"
                    className="u-input"
                    value={editSettings.subtitle}
                    maxLength={60}
                    placeholder={t('chatbotSettings.subtitlePlaceholder', 'répond en quelques secondes')}
                    onChange={(e) => set({ subtitle: e.target.value })}
                  />
                </Field>
              </div>
            </Card>

            <Card
              id="chatbot-theme"
              icon={Palette}
              title={t('chatbotSettings.sectionTheme', 'Thème')}
              description={t('channelsUi.chatbot.themeDesc', 'Une couleur de marque suffit : l’en-tête, la bulle et vos boutons la reprennent.')}
            >
              <div className="u-form">
                <BrandColorPicker
                  value={editSettings.primary_color}
                  lang={lang}
                  onPick={(color) => set({ primary_color: color, secondary_color: '#FFFFFF', tertiary_color: '#F8F9FA' })}
                  labels={{
                    title: t('chatbotSettings.brandColor', 'Couleur de marque'),
                    custom: t('chatbotSettings.customColor', 'Couleur personnalisée'),
                    ok: (ratio) => t('chatbotSettings.contrastOk', 'Contraste AA vérifié ({{ratio}}:1)', { ratio }),
                    warn: (ratio) => t('chatbotSettings.contrastWarn', 'Contraste faible ({{ratio}}:1) — le texte sera peu lisible', { ratio }),
                  }}
                />
                <details className="u-disclosure chan-v2__disclosure">
                  <summary>
                    <ChevronRight aria-hidden="true" />
                    {t('chatbotSettings.advancedColors', 'Réglages avancés — couleurs séparées')}
                  </summary>
                  {/* No « Couleur de l’en-tête »: the widget never draws header_color
                      (chatbotStatic/chatbot.js), its stored value rides along untouched. */}
                  <div className="chan-v2__disclosure-body u-form-grid">
                    <ColorField id="primary_color" label={t('chatbotSettings.primaryColor', 'Couleur principale')} value={editSettings.primary_color} onChange={(v) => set({ primary_color: v })} />
                    <ColorField id="secondary_color" label={t('chatbotSettings.secondaryColor', 'Couleur secondaire')} value={editSettings.secondary_color} onChange={(v) => set({ secondary_color: v })} />
                    <ColorField id="tertiary_color" label={t('chatbotSettings.tertiaryColor', 'Couleur tertiaire')} value={editSettings.tertiary_color} onChange={(v) => set({ tertiary_color: v })} />
                  </div>
                </details>
              </div>
            </Card>

            <Card
              id="chatbot-welcome"
              icon={MessageSquareText}
              title={t('chatbotSettings.sectionWelcome', 'Accueil')}
              description={t('channelsUi.chatbot.welcomeDesc', 'Ce que le client lit en ouvrant le chatbot, et l’invite qui l’attire quand la bulle est fermée.')}
            >
              <div className="u-form">
                <Field label={t('chatbotSettings.welcomeTitle', 'Titre d’accueil')} htmlFor="welcome_title">
                  <input
                    id="welcome_title"
                    className="u-input"
                    value={editSettings.welcome_title}
                    maxLength={80}
                    placeholder={t('chatbotSettings.welcomeTitlePlaceholder', 'Bonjour, comment pouvons-nous vous aider ?')}
                    onChange={(e) => set({ welcome_title: e.target.value })}
                  />
                </Field>
                <Field label={t('chatbotSettings.greetingMessage', 'Message de bienvenue')} htmlFor="greetings_message">
                  <textarea
                    id="greetings_message"
                    className="u-textarea"
                    rows={3}
                    value={editSettings.greetings_message || ''}
                    onChange={(e) => set({ greetings_message: e.target.value })}
                  />
                </Field>
                <Field
                  label={t('chatbotSettings.suggestedQuestions', 'Questions suggérées ({{n}}/4)', { n: questions.length })}
                  htmlFor="chatbot-question"
                  help={t('channelsUi.chatbot.questionsHelp', 'Des raccourcis sur l’accueil : un clic envoie la question. 4 au plus, 80 caractères chacune.')}
                >
                  {questions.length > 0 && (
                    <ul className="chatbot-v2__questions">
                      {questions.map((question, index) => (
                        <li className="u-tag" key={`${question}-${index}`}>
                          {question}
                          <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                            onClick={() => removeSuggestedQuestion(index)}
                            aria-label={t('chatbotSettings.remove', 'Retirer')}
                            title={t('chatbotSettings.remove', 'Retirer')}
                          >
                            <X aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {questions.length < 4 && (
                    <div className="u-inline-form">
                      <input
                        id="chatbot-question"
                        className="u-input"
                        value={questionDraft}
                        maxLength={80}
                        placeholder={t('chatbotSettings.questionPlaceholder', 'Ex. : Suivre ma commande')}
                        onChange={(e) => setQuestionDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSuggestedQuestion(); } }}
                      />
                      <button type="button" className="u-btn u-btn--secondary" onClick={addSuggestedQuestion} disabled={!questionDraft.trim()}>
                        <Plus aria-hidden="true" />
                        {t('chatbotSettings.add', 'Ajouter')}
                      </button>
                    </div>
                  )}
                </Field>
                <div className="chan-v2__section">
                  <div className="chan-v2__switches">
                    <SwitchRow
                      id="teaser_enabled"
                      label={t('chatbotSettings.teaserEnabled', 'Message d’invite (bulle fermée)')}
                      help={t('channelsUi.chatbot.teaserHelp', 'Une bulle de texte apparaît à côté du chatbot fermé, après le délai choisi.')}
                      checked={editSettings.teaser_enabled}
                      onChange={(value) => set({ teaser_enabled: value })}
                    />
                  </div>
                  {editSettings.teaser_enabled && (
                    <div className="u-form-grid">
                      <Field label={t('chatbotSettings.teaserMessage', 'Texte de l’invite')} htmlFor="teaser_message">
                        <input
                          id="teaser_message"
                          className="u-input"
                          value={editSettings.teaser_message}
                          maxLength={140}
                          placeholder={t('chatbotSettings.teaserPlaceholder', 'Une question sur votre commande ? Je suis là.')}
                          onChange={(e) => set({ teaser_message: e.target.value })}
                        />
                      </Field>
                      <RangeField
                        id="teaser_delay_seconds"
                        label={t('channelsUi.chatbot.teaserDelay', 'Délai d’apparition')}
                        value={editSettings.teaser_delay_seconds}
                        unit="s"
                        min={1}
                        max={30}
                        onChange={(v) => set({ teaser_delay_seconds: parseInt(v, 10) || 6 })}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>

            <Card
              id="chatbot-bubble"
              icon={MessageCircle}
              title={t('chatbotSettings.sectionBubble', 'Bulle & lancement')}
              description={t('chatbotSettings.bubbleLogoHint', 'Le logo apparaît automatiquement dans la bulle quand il est défini.')}
            >
              <div className="u-form-grid">
                <Field label={t('chatbotSettings.bubbleShape', 'Forme')} htmlFor="bubble_shape">
                  <select id="bubble_shape" className="u-select" value={editSettings.bubble_shape} onChange={(e) => set({ bubble_shape: e.target.value })}>
                    <option value="circle">{t('chatbotSettings.circle', 'Cercle')}</option>
                    <option value="rounded_square">{t('chatbotSettings.roundedSquare', 'Carré arrondi')}</option>
                  </select>
                </Field>
                <Field label={t('chatbotSettings.bubblePosition', 'Position')} htmlFor="bubble_position">
                  <select id="bubble_position" className="u-select" value={editSettings.bubble_position} onChange={(e) => set({ bubble_position: e.target.value })}>
                    <option value="bottom_right">{t('chatbotSettings.bottomRight', 'En bas à droite')}</option>
                    <option value="bottom_left">{t('chatbotSettings.bottomLeft', 'En bas à gauche')}</option>
                  </select>
                </Field>
                <RangeField
                  id="bubble_size"
                  label={t('chatbotSettings.bubbleSize', 'Taille')}
                  value={editSettings.bubble_size}
                  unit="px"
                  min={40}
                  max={80}
                  onChange={(v) => set({ bubble_size: parseInt(v, 10) || 60 })}
                />
                <Field label={t('chatbotSettings.bubbleShadow', 'Ombre')} htmlFor="bubble_shadow">
                  <select id="bubble_shadow" className="u-select" value={editSettings.bubble_shadow} onChange={(e) => set({ bubble_shadow: e.target.value })}>
                    <option value="none">{t('chatbotSettings.none', 'Aucune')}</option>
                    <option value="default">{t('chatbotSettings.default', 'Par défaut')}</option>
                    <option value="large">{t('chatbotSettings.large', 'Grande')}</option>
                  </select>
                </Field>
                <ColorField
                  id="bubble_border_color"
                  label={t('chatbotSettings.bubbleBorderColor', 'Couleur de bordure')}
                  value={editSettings.bubble_border_color}
                  fallback={editSettings.primary_color}
                  placeholder={t('channelsUi.auto', 'auto')}
                  help={t('channelsUi.chatbot.borderHelp', 'Vide : pas de bordure.')}
                  onChange={(v) => set({ bubble_border_color: v || null })}
                />
              </div>
            </Card>

            <Card
              id="chatbot-install"
              icon={Code}
              title={t('channelsUi.install', 'Installation')}
              description={t('chatbotSettings.embedHint', 'Collez ce code dans le HTML de votre site pour afficher le chatbot.')}
            >
              <div className="u-form">
                <div className="chan-v2__switches">
                  <SwitchRow
                    id="displayed"
                    label={t('chatbotSettings.displayedLabel', 'Afficher le chatbot')}
                    help={t('channelsUi.chatbot.displayedHelp', 'Désactivé : le chatbot disparaît de votre site sans retirer le code.')}
                    checked={editSettings.displayed}
                    onChange={(value) => set({ displayed: value })}
                  />
                  <SwitchRow
                    id="powered_by"
                    label={t('chatbotSettings.poweredBy', '« Propulsé par Unanimity »')}
                    help={t('channelsUi.poweredByHelp', 'La mention discrète en bas du widget.')}
                    checked={editSettings.powered_by}
                    onChange={(value) => set({ powered_by: value })}
                  />
                </div>
                <Field
                  label={t('chatbotSettings.language', 'Langue des textes système')}
                  htmlFor="language"
                  help={t('channelsUi.chatbot.languageHelp', 'Boutons, champ de saisie et mentions du widget. Vos textes restent tels quels.')}
                >
                  <select id="language" className="u-select" value={editSettings.language} onChange={(e) => set({ language: e.target.value })}>
                    <option value="fr">{t('chatbotSettings.languageFr', 'Français')}</option>
                    <option value="en">{t('chatbotSettings.languageEn', 'English')}</option>
                  </select>
                </Field>
                <div className="u-field">
                  <span className="u-label">{t('chatbotSettings.embedCode', 'Code d’intégration')}</span>
                  {selectedChatbotId ? (
                    <EmbedCode
                      code={embed.code}
                      loading={embed.loading}
                      error={embed.error}
                      copyLabel={kit.embed.copy}
                      copiedLabel={kit.embed.copied}
                      loadingLabel={kit.embed.loading}
                    />
                  ) : (
                    <p className="u-help">{t('channelsUi.chatbot.codeAfterCreate', 'Le code apparaît une fois le chatbot créé.')}</p>
                  )}
                </div>
              </div>
            </Card>

            <SaveBar
              dirty={dirty}
              saving={saving}
              canSave={dirty || Boolean(creating)}
              onSave={handleSave}
              onReset={() => { setEditSettings(baseline.settings); setLogoUrl(baseline.logoUrl); }}
              note={hasBrand ? editedName : null}
              labels={{
                ...kit.save,
                save: creating ? t('channelsUi.chatbot.create', 'Créer le chatbot') : kit.save.save,
              }}
            />
          </div>

          <PreviewCard
            title={kit.preview}
            badge={!editSettings.displayed ? <Badge>{kit.hiddenOnSite}</Badge> : null}
            modes={[
              { value: 'home', label: t('chatbotSettings.previewHome', 'Accueil') },
              { value: 'desktop', label: t('chatbotSettings.previewDesktop', 'Conversation') },
              { value: 'mobile', label: t('chatbotSettings.previewMobile', 'Mobile') },
              { value: 'closed', label: t('chatbotSettings.previewClosed', 'Bulle fermée') },
            ]}
            mode={previewMode}
            onMode={setPreviewMode}
            modesLabel={t('chatbotSettings.previewModes', 'Aperçu')}
            note={t('chatbotSettings.previewNote', 'L’aperçu est le rendu réel du widget — essayez d’envoyer un message.')}
          >
            <ChatbotPreview
              primary_color={editSettings.primary_color}
              secondary_color={editSettings.secondary_color}
              header_color={editSettings.header_color}
              company_name={editSettings.company_name}
              agent_name={editSettings.agent_name}
              greetings_message={editSettings.greetings_message}
              subtitle={editSettings.subtitle}
              logo_url={logoUrl}
              suggested_questions={editSettings.suggested_questions}
              teaser_enabled={editSettings.teaser_enabled}
              teaser_message={editSettings.teaser_message}
              powered_by={editSettings.powered_by}
              language={editSettings.language}
              bubble_shape={editSettings.bubble_shape}
              bubble_size={editSettings.bubble_size}
              bubble_position={editSettings.bubble_position}
              bubble_border_color={editSettings.bubble_border_color}
              bubble_shadow={editSettings.bubble_shadow}
              welcome_title={editSettings.welcome_title}
              mode={previewMode}
            />
          </PreviewCard>
        </div>
      </div>

      {pendingEntry && (
        <DiscardDialog
          t={t}
          name={editedName}
          onConfirm={() => { applyEntry(pendingEntry); setPendingEntry(null); }}
          onCancel={() => setPendingEntry(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={t('channelsUi.chatbot.deleteTitle', 'Supprimer le chatbot « {{name}} » ?', { name: editedName })}
          message={t('channelsUi.chatbot.deleteMessage', 'Les sites où son code est installé afficheront le chatbot avec l’apparence par défaut. Les conversations et les tickets sont conservés.')}
          confirmLabel={t('chatbotSettings.delete', 'Supprimer')}
          cancelLabel={kit.cancel}
          icon={Trash2}
          busy={deleteBusy}
          onConfirm={runDelete}
          onCancel={() => setDeleting(false)}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={kit.close}>{toast.text}</Toast>
      )}
    </div>
  );
};

export default ChatbotSettings;
